import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useJointBillServiceProviderBillPlansQuery } from "@services/jointBillServiceProviderBillPlans/queries";
import { useOverviewQuery } from "@services/overview/queries";
import { LT_MAX_PLANS } from "@utils/plans/constants";
import { MaxPlanSelectionPlanCard } from "./components/MaxPlanSelectionPlanCard";

interface IMaxPlanSelectionModalProps extends IUseModalProps {
  jointBillServiceId: number;
  onSuccess?: () => void;
}

export const MaxPlanSelectionModal: React.FC<IMaxPlanSelectionModalProps> = (
  props,
) => {
  const { jointBillServiceId, onSuccess } = props;

  const modal = useModal(props);
  const { t } = useTranslation();

  const { data: overviewData } = useOverviewQuery();

  const { data } =
    useJointBillServiceProviderBillPlansQuery(jointBillServiceId);

  const selectedJointBillServiceDisplayName = useMemo(
    () =>
      overviewData?.jointBillServices.find(
        (jointBillService) =>
          jointBillService.jointBillServiceId === jointBillServiceId,
      )?.displayName ?? t("common.defaultAddress"),
    [overviewData, jointBillServiceId, t],
  );

  const maxPlansOnlyData = useMemo(() => {
    if (!data) return null;

    return data.filter((plan) => LT_MAX_PLANS.has(plan.jointBillServicePlanId));
  }, [data]);

  return (
    <Modal
      modal={modal}
      title={t("plan.modal.maxCards.title")}
      subtitle={
        <React.Fragment>
          {t("plan.modal.maxCards.subtitle")}
          <span className="text-base-black">
            {selectedJointBillServiceDisplayName}
          </span>
        </React.Fragment>
      }
      className="min-w-full lg:w-full xl:min-w-[72rem]"
      titleClassName="text-2xl"
      subtitleClassName="text-base"
    >
      <div className="grid grid-cols-1 gap-6 md:pt-6 lg:grid-cols-3">
        {maxPlansOnlyData?.map((plan) => (
          <MaxPlanSelectionPlanCard
            onComplete={() => {
              modal.close();
              onSuccess?.();
            }}
            key={plan.jointBillServicePlanId}
            jointBillServiceId={jointBillServiceId}
            {...plan}
            isPlanChangeAvailable
          />
        ))}
      </div>
    </Modal>
  );
};
